import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import Toolbar from '../components/quote/Toolbar';
import CompanyHeader from '../components/quote/CompanyHeader';
import QuoteInfo from '../components/quote/QuoteInfo';
import ScopeOfWorks from '../components/quote/ScopeOfWorks';
import PriceSummary from '../components/quote/PriceSummary';
import PaymentSchedule from '../components/quote/PaymentSchedule';
import ExclusionsNotes from '../components/quote/ExclusionsNotes';
import FloatingActionBar from '../components/quote/FloatingActionBar';
import SignaturePicker from '../components/quote/SignaturePicker';
import { getDefaultQuoteData, updateHistory, normalizeExclusions, denormalizeExclusions, normalizePaymentSchedule, DEFAULT_ACCEPTANCE, fetchPaymentDefaults, fetchCompanyInfoWithDefaults } from '../lib/defaultData';
import { BRAND_FULL } from '../lib/brandConfig';
import { generateQuotePDF } from '../lib/pdfExport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DRAFT_KEY = 'revolead_quote_draft';

export default function QuoteEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // When opened from a project, return there instead of the global quotes list
  const fromProject = searchParams.get('from_project');
  const [quoteData, setQuoteData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastAutoSaved, setLastAutoSaved] = useState(null);
  const [relatedInvoices, setRelatedInvoices] = useState([]);
  const autoSaveTimer = useRef(null);
  const serverAutoSaveTimer = useRef(null);
  const lastSavedRef = useRef(null);

  // Initialize
  useEffect(() => {
    const init = async () => {
      if (id) {
        try {
          const res = await axios.get(`${API}/quotes/${id}`);
          const loaded = res.data;
          loaded.exclusions = normalizeExclusions(loaded.exclusions);
          loaded.payment_schedule = normalizePaymentSchedule(loaded.payment_schedule);
          loaded.payment_details = loaded.payment_details || await fetchPaymentDefaults();
          loaded.acceptance = loaded.acceptance || { ...DEFAULT_ACCEPTANCE };
          // Always merge fresh brand from Company Profile
          const { refreshCompanyBrand } = await import('../lib/brandConfig');
          const brand = await refreshCompanyBrand();
          const ci = loaded.company_info || {};
          loaded.company_info = {
            ...ci,
            name: brand?.name || ci.name || '',
            acn: brand?.acn || ci.acn || '',
            logo_url: brand?.logo_url || ci.logo_url || '',
            phone: brand?.phone || ci.phone || '',
            director: brand?.director || ci.director || '',
            address: brand?.address || ci.address || '',
            website: brand?.website || ci.website || '',
            email: brand?.email || ci.email || '',
          };
          setQuoteData(loaded);
        } catch (err) {
          console.error('Failed to load quote:', err);
          toast.error('Failed to load quote');
          navigate('/');
          return;
        }
      } else {
        const draft = localStorage.getItem(DRAFT_KEY);
        let data;
        if (draft) {
          try {
            data = JSON.parse(draft);
            data.exclusions = normalizeExclusions(data.exclusions);
            data.payment_schedule = normalizePaymentSchedule(data.payment_schedule);
          } catch (e) {
            console.warn('Failed to parse draft:', e);
            data = getDefaultQuoteData();
          }
        } else {
          data = getDefaultQuoteData();
        }
        // Fill payment defaults from backend (keeps bank info out of JS bundle)
        if (!data.payment_details?.bank_name) {
          data.payment_details = await fetchPaymentDefaults();
        }
        // S-02: Always merge fresh brand fields from Company Profile
        const { refreshCompanyBrand } = await import('../lib/brandConfig');
        const brand = await refreshCompanyBrand();
        if (!data.company_info) data.company_info = {};
        if (brand) {
          data.company_info = {
            ...data.company_info,
            name: brand.name || data.company_info.name || '',
            acn: brand.acn || data.company_info.acn || '',
            logo_url: brand.logo_url || data.company_info.logo_url || '',
            phone: brand.phone || data.company_info.phone || '',
            director: brand.director || data.company_info.director || '',
            address: brand.address || data.company_info.address || '',
            website: brand.website || data.company_info.website || '',
            email: brand.email || data.company_info.email || '',
          };
        } else {
          // Fallback if brand API fails
          data.company_info = await fetchCompanyInfoWithDefaults();
        }
        // Fetch next quote number if empty
        if (!data.quote_no) {
          try {
            const res = await axios.get(`${API}/quotes/next-number`);
            data.quote_no = res.data.quote_no;
          } catch {
            data.quote_no = 'Q-0000-001';
          }
        }
        setQuoteData(data);
      }
      setIsLoading(false);
    };
    init();
  }, [id, navigate]);

  // Fetch related invoices for this quote
  useEffect(() => {
    if (!id) return;
    const fetchRelatedInvoices = async () => {
      try {
        const res = await axios.get(`${API}/invoices/by-quote/${id}`);
        setRelatedInvoices(res.data || []);
      } catch (err) {
        // Non-critical — silently fail
        console.warn('Could not fetch related invoices:', err);
      }
    };
    fetchRelatedInvoices();
  }, [id]);

  // Sync company_info when brand is updated in Settings
  useEffect(() => {
    const handler = async () => {
      const { refreshCompanyBrand, fetchCompanyDefaults } = await import('../lib/brandConfig');
      const brand = await refreshCompanyBrand();
      const defaults = await fetchCompanyDefaults();
      if (!brand) return;
      setQuoteData(prev => prev ? {
        ...prev,
        company_info: {
          ...prev.company_info,
          name: brand.name || '',
          acn: brand.acn || '',
          logo_url: brand.logo_url || '',
          address: defaults?.company_address || '',
          phone: brand.phone || defaults?.company_phone || '',
          director: brand.director || defaults?.company_director || '',
          website: brand.website || defaults?.company_website || '',
          email: brand.email || defaults?.company_email || '',
        }
      } : prev);
    };
    window.addEventListener('company-brand-updated', handler);
    return () => window.removeEventListener('company-brand-updated', handler);
  }, []);

  // Silent server save — no toast, no setQuoteData (avoids re-render loop)
  const silentSave = useCallback(async (data) => {
    const quoteId = id || data?.id;
    if (!quoteId || !data) return;
    const currentJson = JSON.stringify(data);
    if (lastSavedRef.current === currentJson) return; // nothing changed since last save
    try {
      const payload = { ...data };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.subtotal;
      payload.exclusions = denormalizeExclusions(data.exclusions);
      await axios.put(`${API}/quotes/${quoteId}`, payload);
      lastSavedRef.current = currentJson;
      const now = new Date();
      setLastAutoSaved(`${now.getHours().toString().padStart(2,'0')}:${now.getMinutes().toString().padStart(2,'0')}`);
    } catch (e) {
      // silent — server auto-save failures are not shown to user
    }
  }, [id]);

  // Auto-save: localStorage (1s debounce) + server (30s debounce for existing quotes)
  useEffect(() => {
    if (!quoteData) return;

    // localStorage backup — always, fast
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify(quoteData)); } catch (e) { /* ignore */ }
    }, 1000);

    // Server save — only for existing quotes, debounced 30s
    if (id || quoteData.id) {
      if (serverAutoSaveTimer.current) clearTimeout(serverAutoSaveTimer.current);
      serverAutoSaveTimer.current = setTimeout(() => {
        silentSave(quoteData);
      }, 30000);
    }

    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      if (serverAutoSaveTimer.current) clearTimeout(serverAutoSaveTimer.current);
    };
  }, [quoteData, id, silentSave]);

  // Computed values
  // When amount_enabled is false, use manually entered subtotal instead of summing categories
  const categorySum = quoteData?.categories?.reduce((sum, cat) => sum + (Number(cat.price) || 0), 0) || 0;
  const subtotal = quoteData?.amount_enabled !== false
    ? categorySum
    : (Number(quoteData?.manual_subtotal) || 0);
  const gstAmount = quoteData?.gst_enabled ? subtotal * 0.1 : 0;
  const total = subtotal + gstAmount;
  const paymentTotal = quoteData?.gst_enabled ? total : subtotal;

  const retentionPercentage = 100 - (quoteData?.payment_schedule?.reduce(
    (sum, node) => sum + (Number(node.percentage) || 0), 0
  ) || 0);
  const hasRetentionWarning = retentionPercentage < 0;

  // Update helpers
  const updateField = useCallback((field, value) => {
    setQuoteData(prev => prev ? { ...prev, [field]: value } : prev);
  }, []);

  const updateCompanyInfo = useCallback((field, value) => {
    setQuoteData(prev => prev ? {
      ...prev,
      company_info: { ...prev.company_info, [field]: value },
    } : prev);
  }, []);

  const updatePaymentDetails = useCallback((field, value) => {
    setQuoteData(prev => prev ? {
      ...prev,
      payment_details: { ...prev.payment_details, [field]: value },
    } : prev);
  }, []);

  const updateAcceptance = useCallback((field, value) => {
    setQuoteData(prev => prev ? {
      ...prev,
      acceptance: { ...prev.acceptance, [field]: value },
    } : prev);
  }, []);

  // Save to API
  const saveQuote = async () => {
    if (!quoteData) return;
    // Cancel any pending server auto-save — manual save supersedes it
    if (serverAutoSaveTimer.current) clearTimeout(serverAutoSaveTimer.current);
    setIsSaving(true);
    try {
      const payload = { ...quoteData };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.subtotal;
      payload.exclusions = denormalizeExclusions(quoteData.exclusions);

      if (id || quoteData.id) {
        const quoteId = id || quoteData.id;
        const res = await axios.put(`${API}/quotes/${quoteId}`, payload);
        // Preserve fields the backend may not echo back (e.g. amount_enabled, manual_subtotal
        // were added to the Pydantic model later and may be absent from older Cloud Run revisions)
        // Re-normalize exclusions: server returns string[], component needs {id, text}[]
        setQuoteData({
          ...res.data,
          exclusions: normalizeExclusions(res.data.exclusions),
          amount_enabled: res.data.amount_enabled ?? payload.amount_enabled,
          manual_subtotal: res.data.manual_subtotal ?? payload.manual_subtotal,
          // Preserve text fields that older backend revisions may return as ""
          project_completion: res.data.project_completion || payload.project_completion,
          validity_note: res.data.validity_note || payload.validity_note,
          general_conditions: res.data.general_conditions || payload.general_conditions,
        });
        toast.success('Quote updated');
      } else {
        const res = await axios.post(`${API}/quotes`, payload);
        setQuoteData({
          ...res.data,
          exclusions: normalizeExclusions(res.data.exclusions),
          amount_enabled: res.data.amount_enabled ?? payload.amount_enabled,
          manual_subtotal: res.data.manual_subtotal ?? payload.manual_subtotal,
          project_completion: res.data.project_completion || payload.project_completion,
          validity_note: res.data.validity_note || payload.validity_note,
          general_conditions: res.data.general_conditions || payload.general_conditions,
        });
        navigate(
          fromProject ? `/quote/${res.data.id}?from_project=${fromProject}` : `/quote/${res.data.id}`,
          { replace: true }
        );
        toast.success('Quote saved');
      }
      // Mark current state as saved so auto-save doesn't immediately re-fire
      lastSavedRef.current = JSON.stringify(quoteData);
      // Update autocomplete history
      const catNames = quoteData.categories.map(c => c.name).filter(Boolean);
      const itemDescs = quoteData.categories.flatMap(c => c.items.map(i => i.description)).filter(Boolean);
      updateHistory('history_categories', catNames);
      updateHistory('history_items', itemDescs);
      if (quoteData.client_name) updateHistory('history_clients', [quoteData.client_name]);
      if (quoteData.project_name) updateHistory('history_projects', [quoteData.project_name]);
    } catch (err) {
      console.error('Failed to save:', err);
      toast.error('Failed to save quote');
    }
    setIsSaving(false);
  };

  // Draft operations
  const saveDraft = () => {
    if (!quoteData) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(quoteData));
      toast.success('Draft saved');
    } catch (e) {
      toast.error('Failed to save draft');
    }
  };

  const loadDraft = async () => {
    const draft = localStorage.getItem(DRAFT_KEY);
    if (draft) {
      try {
        const data = JSON.parse(draft);
        data.exclusions = normalizeExclusions(data.exclusions);
        data.payment_schedule = normalizePaymentSchedule(data.payment_schedule);
        data.payment_details = data.payment_details || await fetchPaymentDefaults();
        setQuoteData(data);
        toast.success('Draft loaded');
      } catch {
        toast.error('Invalid draft data');
      }
    } else {
      toast.info('No draft found');
    }
  };

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setQuoteData(getDefaultQuoteData());
    toast.success('Draft cleared');
  };

  // PDF generation
  const handleGeneratePDF = async (mode = 'download') => {
    if (hasRetentionWarning) {
      toast.error('Cannot generate PDF: payment percentages exceed 100%');
      return;
    }
    if (!quoteData) return;

    // Sanitize: ensure all nullable fields have defaults
    const ci = quoteData.company_info || {};
    const sanitized = {
      ...quoteData,
      company_info: {
        name: ci.name || '',
        acn: ci.acn || '',
        address: ci.address || '',
        phone: ci.phone || '',
        director: ci.director || '',
        logo_url: ci.logo_url || '',
        website: ci.website || '',
        email: ci.email || '',
      },
      categories: quoteData.categories || [],
      payment_schedule: quoteData.payment_schedule || [],
      exclusions: quoteData.exclusions || [],
      project_completion: quoteData.project_completion || '',
      validity_note: quoteData.validity_note || '',
      general_conditions: quoteData.general_conditions || '',
      payment_details: quoteData.payment_details || {},
      contact_person: quoteData.contact_person || '',
      client_name: quoteData.client_name || '',
      project_name: quoteData.project_name || '',
      client_abn: quoteData.client_abn || '',
      project_address: quoteData.project_address || '',
    };

    try {
      await generateQuotePDF(sanitized, subtotal, gstAmount, total, paymentTotal, retentionPercentage, mode);
      if (mode === 'download') toast.success('PDF generated');
    } catch (err) {
      console.error('PDF generation failed:', err);
      toast.error('Failed to generate PDF');
    }
  };

  const handlePdfPreview = () => handleGeneratePDF('preview');
  const handlePdfDownload = () => handleGeneratePDF('download');
  const handlePrint = () => window.print();

  if (isLoading || !quoteData) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#e5e4df' }}>
        <div className="text-[#6b7280]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#e5e4df' }}>
      <Toaster position="top-right" />
      <Toolbar
        onSave={saveQuote}
        onSaveDraft={saveDraft}
        onLoadDraft={loadDraft}
        onClearDraft={clearDraft}
        isSaving={isSaving}
        quoteId={id || quoteData.id}
        lastAutoSaved={lastAutoSaved}
        onPdfPreview={handlePdfPreview}
        onPdfDownload={handlePdfDownload}
        onPrint={handlePrint}
        backTo={fromProject ? `/projects/${fromProject}` : '/'}
        backLabel={fromProject ? 'Project' : 'Quotes'}
      />

      <div className="quote-page" data-testid="quote-page">
        <CompanyHeader
          companyInfo={quoteData.company_info}
          onUpdate={updateCompanyInfo}
        />

        <div className="px-10 py-8">
          <QuoteInfo
            quoteData={quoteData}
            onUpdateField={updateField}
            onClientSelect={(client) => {
              // Additional side-effects when a client is selected from CRM
              if (client.trading_name && !quoteData.project_name) {
                updateField('project_name', client.trading_name);
              }
            }}
            onContactSelect={(contact) => {
              // Additional side-effects when a contact is selected from CRM
              if (contact.email) {
                // Could store email for future use; currently no email field on quote
              }
            }}
          />

          <ScopeOfWorks
            categories={quoteData.categories}
            onUpdateCategories={cats => updateField('categories', cats)}
            amountEnabled={quoteData.amount_enabled !== false}
            onToggleAmount={v => updateField('amount_enabled', v)}
          />

          <PriceSummary
            subtotal={subtotal}
            gstEnabled={quoteData.gst_enabled}
            onToggleGst={v => updateField('gst_enabled', v)}
            gstAmount={gstAmount}
            total={total}
            amountEnabled={quoteData.amount_enabled !== false}
            manualSubtotal={Number(quoteData.manual_subtotal) || 0}
            onUpdateManualSubtotal={v => updateField('manual_subtotal', v)}
          />

          <PaymentSchedule
            paymentSchedule={quoteData.payment_schedule}
            onUpdate={ps => updateField('payment_schedule', ps)}
            paymentTotal={paymentTotal}
            retentionPercentage={retentionPercentage}
            hasWarning={hasRetentionWarning}
          />

          {/* Project Completion */}
          <div className="py-8 border-t" style={{ borderColor: 'rgba(201,168,76,0.2)' }}>
            <h2 className="section-heading">Project Completion</h2>
            <textarea
              value={quoteData.project_completion}
              onChange={e => updateField('project_completion', e.target.value)}
              className="editable-field w-full resize-none text-sm leading-relaxed"
              rows={3}
              data-testid="project-completion-input"
            />
          </div>

          <ExclusionsNotes
            exclusions={quoteData.exclusions}
            onUpdate={ex => updateField('exclusions', ex)}
          />

          {/* Validity Note */}
          <div className="py-8 border-t" style={{ borderColor: 'rgba(201,168,76,0.2)' }}>
            <h2 className="section-heading">Quotation Validity</h2>
            <textarea
              value={quoteData.validity_note}
              onChange={e => updateField('validity_note', e.target.value)}
              className="editable-field w-full resize-none text-sm leading-relaxed"
              rows={4}
              data-testid="validity-note-input"
            />
          </div>

          {/* General Conditions */}
          <div className="py-8 border-t" style={{ borderColor: 'rgba(201,168,76,0.2)' }}>
            <h2 className="section-heading">General Conditions</h2>
            <textarea
              value={quoteData.general_conditions}
              onChange={e => updateField('general_conditions', e.target.value)}
              className="editable-field general-conditions-textarea w-full"
              rows={20}
              data-testid="general-conditions-input"
            />
          </div>

          {/* Payment Details */}
          <div className="py-8 border-t" style={{ borderColor: 'rgba(201,168,76,0.2)' }}>
            <h2 className="section-heading">Payment Details</h2>
            <div className="mt-4 rounded-lg border p-6 space-y-4" style={{ borderColor: 'rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.04)' }}>
              {[
                { label: 'Bank', field: 'bank_name' },
                { label: 'A/C Name', field: 'account_name' },
                { label: 'BSB', field: 'bsb' },
                { label: 'A/C #', field: 'account_number' },
              ].map(({ label, field }) => (
                <div key={field} className="flex items-center gap-4">
                  <span className="text-sm font-semibold w-24 shrink-0" style={{ color: 'var(--color-navy)' }}>
                    {label}
                  </span>
                  <input
                    type="text"
                    value={(quoteData.payment_details || {})[field] || ''}
                    onChange={e => updatePaymentDetails(field, e.target.value)}
                    className="editable-field flex-1"
                    data-testid={`payment-details-${field}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Acceptance of Quotation */}
          <div className="py-8 border-t" style={{ borderColor: 'rgba(201,168,76,0.2)' }}>
            <h2 className="section-heading">Acceptance of Quotation</h2>
            <p className="text-sm mt-1 mb-6" style={{ color: '#6b7280' }}>
              By signing below, both parties confirm acceptance of this quotation and agree to the terms and conditions outlined above.
            </p>
            <div className="grid grid-cols-2 gap-12 mt-4">
              {/* Client column */}
              <div className="space-y-5">
                <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-navy)' }}>Client</h3>
                <div>
                  <label className="text-xs uppercase tracking-wider block mb-1" style={{ color: '#6b7280' }}>Name</label>
                  <input
                    type="text"
                    value={(quoteData.acceptance || {}).client_name || ''}
                    onChange={e => updateAcceptance('client_name', e.target.value)}
                    className="editable-field w-full text-sm"
                    placeholder="Client name"
                    data-testid="acceptance-client-name"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider block mb-1" style={{ color: '#6b7280' }}>Date</label>
                  <input
                    type="date"
                    value={(quoteData.acceptance || {}).date || ''}
                    onChange={e => updateAcceptance('date', e.target.value)}
                    className="editable-field w-full text-sm"
                    data-testid="acceptance-client-date"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider block mb-1" style={{ color: '#6b7280' }}>Signature</label>
                  <SignaturePicker
                    value={(quoteData.acceptance || {}).client_signature || ''}
                    onChange={v => updateAcceptance('client_signature', v)}
                  />
                </div>
              </div>

              {/* Company column */}
              <div className="space-y-5">
                <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-navy)' }}>{BRAND_FULL}</h3>
                <div>
                  <label className="text-xs uppercase tracking-wider block mb-1" style={{ color: '#6b7280' }}>Authorised By</label>
                  <div className="text-sm py-2" style={{ color: 'var(--color-navy)', fontWeight: 600 }}>{quoteData.company_info?.director || ''}</div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider block mb-1" style={{ color: '#6b7280' }}>Date</label>
                  <input
                    type="date"
                    value={(quoteData.acceptance || {}).authorised_date || ''}
                    onChange={e => updateAcceptance('authorised_date', e.target.value)}
                    className="editable-field w-full text-sm"
                    data-testid="acceptance-authorised-date"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider block mb-1" style={{ color: '#6b7280' }}>Signature</label>
                  <SignaturePicker
                    value={(quoteData.acceptance || {}).company_signature || ''}
                    onChange={v => updateAcceptance('company_signature', v)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingActionBar
        hasWarning={hasRetentionWarning}
        relatedInvoices={relatedInvoices}
        onOpenInvoice={(invId) => navigate(`/invoice/${invId}`)}
      />

    </div>
  );
}
